// useAuth.ts - Student authentication hook with single validation per session
import { useState, useEffect, createContext, useContext, useCallback, type ReactNode } from 'react';
import type { StudentSession } from '../types';
import { checkSession } from '../services/conceptPlus1Service';
import { setAuthErrorHandler } from '../services/supabaseClient';

const SESSION_KEY = 'rewise_plus1_session';

// Track if we've already validated this session in the current app lifecycle
let hasValidatedThisSession = false;

export function getStoredSession(): StudentSession | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    const session = JSON.parse(stored) as StudentSession;

    // Check if session is expired
    if (new Date(session.expires_at) < new Date()) {
      localStorage.removeItem(SESSION_KEY);
      hasValidatedThisSession = false;
      return null;
    }

    // Check if grade is 11 or 12 (Plus1 only)
    if (session.grade_level < 11 || session.grade_level > 12) {
      localStorage.removeItem(SESSION_KEY);
      hasValidatedThisSession = false;
      return null;
    }

    // Migration: If session doesn't have planType, clear it to force re-login
    if (!session.planType) {
      console.log('Session missing planType, clearing to force re-login');
      localStorage.removeItem(SESSION_KEY);
      hasValidatedThisSession = false;
      return null;
    }

    return session;
  } catch {
    return null;
  }
}

export function setStoredSession(session: StudentSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  // Fresh login = mark as validated (no need to call RPC again)
  hasValidatedThisSession = true;
}

export function clearStoredSession(): void {
  localStorage.removeItem(SESSION_KEY);
  hasValidatedThisSession = false;
}

/**
 * Validate session against backend - checks if student still exists in DB
 * This is called ONCE per app lifecycle, not on every route change
 */
async function validateSessionWithBackend(session: StudentSession): Promise<boolean> {
  try {
    const result = await checkSession(session.student_id);
    return result.active === true;
  } catch {
    return false;
  }
}

// Auth Context types
interface AuthContextType {
  session: StudentSession | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (session: StudentSession) => void;
  logout: () => void;
}

// Create Auth Context
const AuthContext = createContext<AuthContextType | null>(null);

/**
 * Auth Provider - validates session ONCE on app bootstrap
 * Wrap your app with this provider in main.tsx
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StudentSession | null>(() => getStoredSession());
  const [isLoading, setIsLoading] = useState(() => {
    const stored = getStoredSession();
    // Only show loading if we have a session that needs validation
    return !!stored && !hasValidatedThisSession;
  });

  // Validate session ONCE on app bootstrap
  useEffect(() => {
    // Skip if no session, not loading, or already validated
    if (!session || !isLoading || hasValidatedThisSession) {
      setIsLoading(false);
      return;
    }

    validateSessionWithBackend(session).then((isValid) => {
      hasValidatedThisSession = true;

      if (!isValid) {
        // Session invalid - user no longer exists in DB
        clearStoredSession();
        setSession(null);
      }
      setIsLoading(false);
    });
  }, []); // Empty deps - run ONCE on mount

  const login = useCallback((newSession: StudentSession) => {
    setStoredSession(newSession);
    setSession(newSession);
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    clearStoredSession();
    setSession(null);
    window.location.href = '/login';
  }, []);

  // Register global 401 handler
  useEffect(() => {
    setAuthErrorHandler(logout);
    return () => setAuthErrorHandler(() => {});
  }, [logout]);

  const value: AuthContextType = {
    session,
    isLoading,
    isAuthenticated: !!session && !isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook for student authentication
 * Reads from AuthContext - no RPC calls on route changes
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
